import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Home, ArrowLeft, AlertTriangle } from 'lucide-react';
import { notFoundPageAPI } from '../services/api';

const NotFound = () => {
  const [pageData, setPageData] = useState({
    title: 'Page Not Found',
    subtitle: 'Oops! The page you are looking for does not exist.',
    description: 'The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.',
    error_code: '404',
    danger_icon: 'AlertTriangle',
    primary_button_text: 'Go Back Home',
    primary_button_icon: 'Home',
    primary_button_link: '/',
    secondary_button_text: 'Search Our Site',
    secondary_button_icon: 'Search',
    search_placeholder: 'Search for pages...',
    background_animation: true,
    decorative_elements: true
  });

  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Fetch page data from API
    const fetchPageData = async () => {
      try {
        const response = await notFoundPageAPI.getContent();
        if (response?.data?.success) {
          const apiData = response.data.data;
          setPageData({
            title: apiData.title || 'Page Not Found',
            subtitle: apiData.subtitle || 'Oops! The page you are looking for does not exist.',
            description: apiData.description || 'The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.',
            error_code: apiData.error_code || '404',
            danger_icon: apiData.danger_icon || 'AlertTriangle',
            primary_button_text: apiData.primary_button_text || 'Go Back Home',
            primary_button_icon: apiData.primary_button_icon || 'Home',
            primary_button_link: apiData.primary_button_link || '/',
            secondary_button_text: apiData.secondary_button_text || 'Search Our Site',
            secondary_button_icon: apiData.secondary_button_icon || 'Search',
            search_placeholder: apiData.search_placeholder || 'Search for pages...',
            background_animation: apiData.background_animation !== false,
            decorative_elements: apiData.decorative_elements !== false
          });
        }
      } catch (error) {
        console.error('Error fetching Not Found page data:', error);
      }
    };

    fetchPageData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // TODO: Implement search functionality
      console.log('Searching for:', searchQuery);
    }
  };

  const handleGoHome = () => {
    window.location.href = pageData.primary_button_link || '/';
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const getIconComponent = (iconName) => {
    const icons = {
      'Home': Home,
      'Search': Search,
      'ArrowLeft': ArrowLeft,
      'AlertTriangle': AlertTriangle
    };
    return icons[iconName] || AlertTriangle;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900 pt-20">
      {/* Animated Background */}
      {pageData.background_animation && (
        <div className="absolute inset-0 pt-20">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-purple-600/5 dark:from-blue-600/10 dark:to-purple-600/10"></div>
          <div className="absolute top-0 left-0 w-full h-full">
            {[...Array(15)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-blue-400/20 dark:bg-blue-300/20 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -100, 0],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>
        </div>
      )}

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12">
        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-center max-w-2xl mx-auto"
        >
          {/* Error Code */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, duration: 0.8, type: "spring" }}
            className="mb-8"
          >
            <div className="relative inline-block">
              <div className="text-8xl md:text-9xl font-bold text-gray-200 dark:text-gray-700">
                {pageData.error_code}
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                {(() => {
                  const IconComponent = getIconComponent(pageData.danger_icon);
                  return <IconComponent className="w-16 h-16 text-red-500 dark:text-red-400" />;
                })()}
              </div>
            </div>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4"
          >
            {pageData.title}
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="text-xl text-gray-600 dark:text-gray-300 mb-6"
          >
            {pageData.subtitle}
          </motion.p>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="text-gray-600 dark:text-gray-300 mb-8"
          >
            {pageData.description}
          </motion.p>

          {/* Search Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mb-8"
          >
            <form onSubmit={handleSearch} className="max-w-md mx-auto">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={pageData.search_placeholder}
                  className="w-full px-4 py-3 pl-12 pr-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              </div>
            </form>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button
              onClick={handleGoHome}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {(() => {
                const IconComponent = getIconComponent(pageData.primary_button_icon);
                return <IconComponent className="w-5 h-5" />;
              })()}
              {pageData.primary_button_text}
            </button>
            
            <button
              onClick={handleGoBack}
              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {(() => {
                const IconComponent = getIconComponent(pageData.secondary_button_icon);
                return <IconComponent className="w-5 h-5" />;
              })()}
              {pageData.secondary_button_text}
            </button>
          </motion.div>
        </motion.div>

        {/* Decorative Elements */}
        {pageData.decorative_elements && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5, duration: 1 }}
              className="absolute bottom-8 left-8 text-gray-400 dark:text-gray-600"
            >
              <div className="text-sm">Error Code: {pageData.error_code}</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.7, duration: 1 }}
              className="absolute bottom-8 right-8 text-gray-400 dark:text-gray-600"
            >
              <div className="text-sm">Page Not Found</div>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
};

export default NotFound; 